"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useAuth } from "@/lib/auth";

/**
 * Listens for Next.js route changes and fires sfAnalytics.trackNavigation().
 * Also syncs user identity to the tracker when auth state changes,
 * and updates the session record in the DB with user identity.
 */
export default function AnalyticsTracker() {
  const pathname = usePathname();
  const { user } = useAuth();
  const isFirst = useRef(true);

  // Sync user identity to analytics tracker + update session record in DB
  useEffect(() => {
    if (user) {
      window.sfAnalytics?.setUser(user.id, user.email, user.role === "superadmin");

      // Update the session record in DB with user identity
      const sid = window.sfAnalytics?.sessionId;
      if (sid) {
        try {
          const payload = JSON.stringify({
            session_id: sid,
            user_id: user.id,
            user_email: user.email,
            is_admin: user.role === "superadmin",
          });
          if (navigator.sendBeacon) {
            navigator.sendBeacon(
              "/api/analytics/collect/update-session-user",
              new Blob([payload], { type: "application/json" })
            );
          }
        } catch {
          // Ignore errors — analytics should never break the app
        }
      }
    } else {
      window.sfAnalytics?.clearUser();
    }
  }, [user]);

  // Track SPA navigations (skip initial — sf-analytics.js handles it)
  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    window.sfAnalytics?.trackNavigation();
  }, [pathname]);

  return null;
}
